import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import {
  EmailOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: Location; message?: string })?.from?.pathname ?? '/';
  const successMessage = (location.state as { message?: string } | null)?.message ?? '';

  const isValid = email.trim() !== '' && password.length >= 1;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || isSubmitting) return;
    setError('');
    setNotice('');
    setIsSubmitting(true);

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = () => {
    setError('');
    setNotice('Contact support to reset your password.');
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex' }}>
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: 1,
          flexDirection: 'column',
          justifyContent: 'space-between',
          px: 7,
          py: 6,
          background: 'linear-gradient(160deg, #0a1628 0%, #102040 60%, #0d2550 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', inset: 0, opacity: 0.22, background: 'linear-gradient(135deg, transparent 0%, rgba(96,165,250,0.45) 100%)' }} />
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: 'white', letterSpacing: '4px', textTransform: 'uppercase', zIndex: 1 }}>
          SeeVita
        </Typography>
        <Box sx={{ zIndex: 1 }}>
          <Typography sx={{ fontSize: '3rem', fontWeight: 800, color: 'white', lineHeight: 1.1, mb: 2.5 }}>
            Your health,
            <br />
            <Box component="span" sx={{ background: 'linear-gradient(90deg, #60a5fa, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              reimagined.
            </Box>
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.95rem', maxWidth: 320, lineHeight: 1.7 }}>
            Secure access for AI-powered vital sign monitoring.
          </Typography>
        </Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.78rem', zIndex: 1 }}>
          Copyright 2026 SeeVita. All rights reserved.
        </Typography>
      </Box>

      <Box
        sx={{
          flex: { xs: 1, md: '0 0 480px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, sm: 6 },
          py: 6,
          bgcolor: '#ffffff',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 360 }}>
          <Typography sx={{ display: { xs: 'block', md: 'none' }, fontSize: '0.75rem', fontWeight: 800, color: '#0a1628', letterSpacing: '4px', textTransform: 'uppercase', mb: 5 }}>
            SeeVita
          </Typography>

          <Typography sx={{ fontSize: '1.85rem', fontWeight: 800, color: '#0a1628', mb: 0.8 }}>
            Sign in
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: '0.88rem', mb: 4 }}>
            Good to see you again.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontSize: '0.85rem' }}>{error}</Alert>}
          {(notice || successMessage) && (
            <Alert severity={successMessage ? 'success' : 'info'} sx={{ mb: 3, borderRadius: '12px', fontSize: '0.85rem' }}>
              {successMessage || notice}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email address"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
              required
              autoComplete="email"
              autoFocus
              InputProps={{ startAdornment: <FieldIcon icon={<EmailOutlined />} active={Boolean(email)} /> }}
              sx={fieldSx}
            />

            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isSubmitting}
              required
              autoComplete="current-password"
              InputProps={{
                startAdornment: <FieldIcon icon={<LockOutlined />} active={Boolean(password)} />,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((v) => !v)} tabIndex={-1} edge="end" sx={{ color: '#94a3b8', '&:hover': { color: '#0a1628' } }}>
                      {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />

            <Box sx={{ textAlign: 'right', mt: -0.5 }}>
              <Button
                type="button"
                onClick={handleForgotPassword}
                disabled={isSubmitting}
                sx={forgotPasswordButtonSx}
              >
                Forgot password?
              </Button>
            </Box>

            <Button type="submit" fullWidth variant="contained" disabled={!isValid || isSubmitting} sx={submitSx}>
              {isSubmitting ? <CircularProgress size={20} sx={{ color: 'rgba(255,255,255,0.8)' }} /> : 'Sign in'}
            </Button>
          </Box>

          <Typography sx={{ mt: 3, color: '#64748b', fontSize: '0.86rem', textAlign: 'center' }}>
            Don't have an account? <Link component={RouterLink} to="/signup" sx={authLinkSx}>Sign up</Link>
          </Typography>
          <Typography sx={{ mt: 1.5, color: '#94a3b8', fontSize: '0.82rem', textAlign: 'center' }}>
            Interested in SeeVita?{' '}
            <Link component={RouterLink} to="/waitlist" sx={{ ...authLinkSx, color: '#0ea5e9', fontSize: '0.82rem' }}>
              Join the waitlist
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const FieldIcon: React.FC<{ icon: React.ReactElement; active: boolean }> = ({ icon, active }) => (
  <InputAdornment position="start">
    {React.cloneElement(icon, { sx: { fontSize: 18, color: active ? '#0a1628' : '#cbd5e1' } })}
  </InputAdornment>
);

const authLinkSx = {
  fontSize: '0.86rem',
  color: '#2563eb',
  cursor: 'pointer',
  fontWeight: 700,
  textDecoration: 'none',
  '&:hover': { color: '#1d4ed8', textDecoration: 'underline' },
};

const forgotPasswordButtonSx = {
  minWidth: 0,
  p: 0,
  fontSize: '0.86rem',
  color: '#2563eb',
  fontWeight: 700,
  textTransform: 'none',
  textDecoration: 'none',
  '&:hover': { color: '#1d4ed8', textDecoration: 'underline', bgcolor: 'transparent' },
  '&.Mui-disabled': { color: '#94a3b8' },
};

export const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    color: '#0a1628',
    fontSize: '0.95rem',
    background: '#f8fafc',
    transition: 'all 0.2s ease',
    '& fieldset': { borderColor: '#e2e8f0', borderWidth: '1.5px' },
    '&:hover fieldset': { borderColor: '#94a3b8' },
    '&.Mui-focused': { background: '#fff' },
    '&.Mui-focused fieldset': {
      borderColor: '#0a1628',
      borderWidth: '1.5px',
      boxShadow: '0 0 0 3px rgba(10,22,40,0.07)',
    },
  },
  '& .MuiInputLabel-root': { color: '#64748b', fontSize: '0.9rem' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#0a1628' },
  '& .MuiFormHelperText-root': { mx: 0, fontSize: '0.78rem' },
};

export const submitSx = {
  mt: 0.5,
  py: 1.65,
  borderRadius: '14px',
  fontSize: '0.95rem',
  fontWeight: 700,
  textTransform: 'none',
  background: 'linear-gradient(135deg, #0a1628 0%, #102040 100%)',
  boxShadow: '0 4px 20px rgba(10,22,40,0.25)',
  transition: 'all 0.25s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #102040 0%, #1a3a6a 100%)',
    boxShadow: '0 8px 28px rgba(10,22,40,0.35)',
    transform: 'translateY(-1px)',
  },
  '&:active': { transform: 'translateY(0)' },
  '&.Mui-disabled': { bgcolor: '#f1f5f9', color: '#cbd5e1', boxShadow: 'none' },
};

export default LoginPage;
